// src/App.jsx
import { useState } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import { useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { warmUpConnection } from './api';
import { warmUpScanner } from './hooks/useScanner';

import Auth         from './pages/Auth';
import EmployeeAuth from './pages/EmployeeAuth';
import ModeSelect   from './pages/ModeSelect';
import ShopView     from './pages/ShopView';
import ProductView  from './pages/ProductView';
import CartView     from './pages/CartView';
import BlockedScreen from './components/BlockedScreen';
import AnnouncementPopup from './components/AnnouncementPopup';

// Shop
import ShopHome       from './pages/shop/ShopHome';
import ShopProducts   from './pages/shop/ShopProducts';
import ShopPrintCatalog from './pages/shop/ShopPrintCatalog';
import ShopPerishable from './pages/shop/ShopPerishable';
import ShopCategories from './pages/shop/ShopCategories';
import ShopCashier    from './pages/shop/ShopCashier';
import ShopAnalytics  from './pages/shop/ShopAnalytics';
import ShopReports    from './pages/shop/ShopReports';
import ShopReportSettings from './pages/shop/ShopReportSettings';
import ShopReport     from './pages/shop/ShopReport';
import ShopSettings   from './pages/shop/ShopSettings';
import ShopEmployees     from './pages/shop/ShopEmployees';
import ShopCounterparties from './pages/shop/ShopCounterparties';
import ShopCounterpartyCard from './pages/shop/ShopCounterpartyCard';
import ShopWholesaleSale from './pages/shop/ShopWholesaleSale';
import ShopPurchase from './pages/shop/ShopPurchase';
import { ShopPurchaseList, ShopPurchaseDetail } from './pages/shop/ShopPurchaseList';
import ShopPurchaseReceive from './pages/shop/ShopPurchaseReceive';
import ShopWholesaleSaleDetail from './pages/shop/ShopWholesaleSaleDetail';
import ShopWaiter from './pages/shop/ShopWaiter';
import ShopWaiterNew from './pages/shop/ShopWaiterNew';
import ShopWaiterOrder from './pages/shop/ShopWaiterOrder';
import ShopKitchen from './pages/shop/ShopKitchen';
import ShopWaiterHistory from './pages/shop/ShopWaiterHistory';
import ShopAiTools       from './pages/shop/ShopAiTools';
import ShopAiHistory  from './pages/shop/ShopAiHistory';
import ShopAiAssistant from './pages/shop/ShopAiAssistant';
import ShopAuditLog   from './pages/shop/ShopAuditLog';
import ShopReportJournal from './pages/shop/ShopReportJournal';
import ShopDebtors     from './pages/shop/ShopDebtors';
import ShopDebtorCard  from './pages/shop/ShopDebtorCard';
import ShopMarkingChecker from './pages/shop/ShopMarkingChecker';
import ShopInventory from './pages/shop/ShopInventory';
import RequireModule  from './components/RequireModule';

// Customer
import CustomerHome    from './pages/customer/CustomerHome';
import CustomerCards   from './pages/customer/CustomerCards';
import CustomerCatalog from './pages/customer/CustomerCatalog';
import CustomerScan    from './pages/customer/CustomerScan';
import CustomerPay     from './pages/customer/CustomerPay';
import CustomerProfile from './pages/customer/CustomerProfile';
import CustomerReceipt from './pages/customer/CustomerReceipt';
import CustomerDebts, { CustomerDebtDetail } from './pages/customer/CustomerDebts';

// Guest
import GuestHome from './pages/guest/GuestHome';

// Стартовая последовательность экранов до логина:
//   'select'   — ModeSelect (кто ты)
//   'customer' — Auth с role='customer'
//   'shop'     — Auth с role='shop'
//   'employee' — EmployeeAuth
function PreAuthFlow() {
  const [step, setStep] = useState('select');

  const back = () => setStep('select');

  if (step === 'customer') return <Auth role="customer" onBack={back} />;
  if (step === 'shop')     return <Auth role="shop"     onBack={back} />;
  if (step === 'employee') return <EmployeeAuth onBack={back} />;

  return <ModeSelect onPick={setStep} />;
}

function AppRoutes() {
  const { user, mode, loading, isGuest, blockedInfo } = useAuth();
  useTheme();

  // Прогрев соединения: заранее находим рабочий хост API (прямой IPv4 или
  // Cloudflare), чтобы первый логин/запрос шёл по готовому пути без задержки.
  useEffect(() => {
    warmUpConnection().catch(() => {});
    warmUpScanner();
  }, []);

  // Аппаратная кнопка «назад» (Android): возвращаемся на предыдущий экран,
  // а приложение закрываем только с главной. Через dynamic import + await —
  // надёжнее прежнего require/.then, который в части версий Capacitor падал.
  useEffect(() => {
    let handle = null;
    let cancelled = false;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const sub = await App.addListener('backButton', () => {
          // Экран может перехватить «назад» (полноэкранная касса: сначала выход из полного экрана)
          const ev = new CustomEvent('alsatuu:back', { cancelable: true });
          if (!window.dispatchEvent(ev)) return;
          const h = window.location.hash || '';
          const atRoot = (h === '' || h === '#/' || h === '#');
          if (!atRoot) {
            window.history.back();
          } else {
            App.exitApp();
          }
        });
        if (cancelled) sub?.remove?.(); else handle = sub;
      } catch (e) {
        // @capacitor/app не установлен — оставляем поведение по умолчанию
      }
    })();
    return () => { cancelled = true; handle?.remove?.(); };
  }, []);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  // Заблокирован — показываем экран блокировки поверх всего
  if (blockedInfo) return <BlockedScreen />;

  // Не залогинен и не гость — показываем стартовый экран
  if (!user && !isGuest) return <PreAuthFlow />;

  // Гость
  if (isGuest) {
    return (
      <Routes>
        <Route path="/" element={<GuestHome />} />
        <Route path="/shop/:shopId" element={<ShopView />} />
        <Route path="/shop/:shopId/product/:productId" element={<ProductView />} />
        <Route path="/shop/:shopId/cart" element={<CartView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Магазин (включая сотрудника)
  if (mode === 'shop') {
    return (
      <Routes>
        <Route path="/" element={<ShopHome />} />
        <Route path="/products" element={<RequireModule module="products"><ShopProducts /></RequireModule>} />
        <Route path="/print-catalog" element={<RequireModule module="ownBarcodes"><ShopPrintCatalog /></RequireModule>} />
        <Route path="/perishable" element={<RequireModule module="perishable"><ShopPerishable /></RequireModule>} />
        <Route path="/categories" element={<RequireModule module="categories"><ShopCategories /></RequireModule>} />
        <Route path="/cashier" element={<RequireModule module="cashier"><ShopCashier /></RequireModule>} />
        <Route path="/analytics" element={<RequireModule module="analytics"><ShopAnalytics /></RequireModule>} />
        <Route path="/report-journal" element={<RequireModule module="analytics"><ShopReportJournal /></RequireModule>} />
        <Route path="/debtors" element={<RequireModule module="retailDebt"><ShopDebtors /></RequireModule>} />
        <Route path="/debtors/:id" element={<RequireModule module="retailDebt"><ShopDebtorCard /></RequireModule>} />
        <Route path="/marking-checker" element={<RequireModule module="marking"><ShopMarkingChecker /></RequireModule>} />
        <Route path="/inventory" element={<RequireModule module="stocktake"><ShopInventory /></RequireModule>} />
        <Route path="/shop/reports" element={<RequireModule module="analytics"><ShopReports /></RequireModule>} />
        <Route path="/shop/reports/settings" element={<RequireModule module="analytics"><ShopReportSettings /></RequireModule>} />
        <Route path="/report" element={<RequireModule module="analytics"><ShopReport /></RequireModule>} />
        <Route path="/settings" element={<ShopSettings />} />
        <Route path="/employees" element={<RequireModule module="employees"><ShopEmployees /></RequireModule>} />
        <Route path="/counterparties" element={<RequireModule module="counterparties"><ShopCounterparties /></RequireModule>} />
        <Route path="/counterparty/:id" element={<RequireModule module="counterparties"><ShopCounterpartyCard /></RequireModule>} />
        <Route path="/wholesale-sale" element={<RequireModule module="wholesale"><ShopWholesaleSale /></RequireModule>} />
        <Route path="/wholesale-sale/:id" element={<RequireModule module="wholesale"><ShopWholesaleSaleDetail /></RequireModule>} />
        <Route path="/purchase" element={<RequireModule module="purchases"><ShopPurchase /></RequireModule>} />
        <Route path="/purchases" element={<RequireModule module="purchases"><ShopPurchaseList /></RequireModule>} />
        <Route path="/purchase/:id" element={<RequireModule module="purchases"><ShopPurchaseDetail /></RequireModule>} />
        <Route path="/purchase/:id/receive" element={<RequireModule module="purchases"><ShopPurchaseReceive /></RequireModule>} />
        <Route path="/waiter" element={<RequireModule module="waiter"><ShopWaiter /></RequireModule>} />
        <Route path="/waiter/new" element={<RequireModule module="waiter"><ShopWaiterNew /></RequireModule>} />
        <Route path="/waiter/history" element={<RequireModule module="waiter"><ShopWaiterHistory /></RequireModule>} />
        <Route path="/waiter/order/:id" element={<RequireModule module="waiter"><ShopWaiterOrder /></RequireModule>} />
        <Route path="/kitchen" element={<RequireModule module="kitchen"><ShopKitchen /></RequireModule>} />
        <Route path="/ai-tools" element={<RequireModule module="ai"><ShopAiTools /></RequireModule>} />
        <Route path="/ai-history" element={<RequireModule module="ai"><ShopAiHistory /></RequireModule>} />
        <Route path="/ai-assistant" element={<RequireModule module="aiAssistant"><ShopAiAssistant /></RequireModule>} />
        <Route path="/audit" element={<ShopAuditLog />} />
        <Route path="/shop/:shopId" element={<ShopView />} />
        <Route path="/shop/:shopId/product/:productId" element={<ProductView />} />
        <Route path="/shop/:shopId/cart" element={<CartView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Покупатель
  if (mode === 'customer') {
    return (
      <Routes>
        <Route path="/" element={<CustomerHome />} />
        <Route path="/cards" element={<CustomerCards />} />
        <Route path="/catalog" element={<CustomerCatalog />} />
        <Route path="/scan" element={<CustomerScan />} />
        <Route path="/receipt/:id" element={<CustomerReceipt />} />
        <Route path="/pay" element={<CustomerPay />} />
        <Route path="/profile" element={<CustomerProfile />} />
        <Route path="/my-debts" element={<CustomerDebts />} />
        <Route path="/my-debts/:cpId" element={<CustomerDebtDetail />} />
        <Route path="/shop/:shopId" element={<ShopView />} />
        <Route path="/shop/:shopId/product/:productId" element={<ProductView />} />
        <Route path="/shop/:shopId/cart" element={<CartView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Fallback — теоретически не должно достигаться
  return <PreAuthFlow />;
}

// Попап объявлений — поверх любого режима (магазин, покупатель, гость)
function Announcements() {
  const { user, mode, loading, isGuest, blockedInfo } = useAuth();
  if (loading || blockedInfo || (!user && !isGuest)) return null;
  const audience = isGuest ? null : (mode === 'shop' || mode === 'customer' ? mode : null);
  return <AnnouncementPopup key={audience || 'all'} audience={audience} />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <HashRouter>
          <AppRoutes />
          <Announcements />
        </HashRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
